import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import {
  BlueberryFeatures,
  FeatureEnum,
} from '../../services/data/impl/cube/cubeSlice';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { useBlueberryFeatureIcon } from '../commonUI/BlueberryIcons';
import { MeasurementTypes, useUnitSymbol } from '../commonUtils';
import { roundFractional } from '../ranches/ranchUtils';
import { useBlueberryBlockAverage } from '../scanDataApi/hooks/useBlueberryBlockAverage';
import { useBlueberryFlowerFeatures } from '../scanDataApi/hooks/useBlueberryFlowerFeatures';
import {
  BlueberryFilterEnum,
  useBlueberryQuery,
} from '../scanDataApi/hooks/useBlueberryQuery';
import { orderBlueberriesByRipenessOrder } from '../scanDataApi/scanDataUtils';
interface tableRowData {
  key: FeatureEnum | string;
  icon?: ReactNode;
  count: number;
  countPerDistance: string;
  average: string;
}

export const BlueberryPhotoCount = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const getIcon = useBlueberryFeatureIcon();

  const meterOrFeetUnitSymbol = useUnitSymbol(MeasurementTypes.meterOrFeet);
  const flowerFeatures = useBlueberryFlowerFeatures();

  const { data: blueberryPointData, isLoading: isBlueberryPointLoading } =
    useBlueberryQuery({
      activeFilters: [BlueberryFilterEnum.point_id],
    });

  const {
    data: blueberryBlockAvgData,
    isLoading: isBlueberryBlockAvgLoading,
    error: blueberryBlockAvgError,
  } = useBlueberryBlockAverage();

  const [totalBlueberries, totalFlowers, rows, summaryRow]: [
    number,
    number,
    tableRowData[],
    tableRowData | null
  ] = useMemo(() => {
    const pointData = blueberryPointData?.at(0);
    const blockAverageData = blueberryBlockAvgData?.at(0);

    if (!pointData || !blockAverageData) {
      return [0, 0, [], null];
    }

    const createRowData = (feature: FeatureEnum) => {
      const count =
        pointData[feature as FeatureEnum.blueberry_Blue]?.raw_count || 0;
      const countPerDistance =
        pointData[feature as FeatureEnum.blueberry_Blue]?.count_per_meter || 0;
      const blockAverage =
        blockAverageData[feature as FeatureEnum.blueberry_Blue]?.block_avg || 0;

      return {
        key: feature,
        icon: (
          <Box
            sx={{
              display: 'flex',
              height: '1.25rem',
              width: '1.25rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {getIcon(feature, 0.9)}
          </Box>
        ),
        count: count,
        countPerDistance: roundFractional(countPerDistance, 1),
        average: roundFractional(blockAverage, 2),
      };
    };

    let totalBlueberries = 0;
    let totalFlowers = 0;
    let totalCountPerDistance = 0;
    let totalBlockAvg = 0;

    const blueberryRows = BlueberryFeatures.map((feature) => {
      const row = createRowData(feature);
      totalBlueberries = totalBlueberries + row.count;
      totalCountPerDistance =
        totalCountPerDistance +
        (pointData[feature as FeatureEnum.blueberry_Blue]?.count_per_meter ||
          0);
      totalBlockAvg =
        totalBlockAvg +
        (blockAverageData[feature as FeatureEnum.blueberry_Blue]?.block_avg ||
          0);
      return row;
    });

    const flowerRows = flowerFeatures.map((feature) => {
      const row = createRowData(feature);
      totalFlowers = totalFlowers + row.count;
      totalCountPerDistance =
        totalCountPerDistance +
        (pointData[feature as FeatureEnum.blueberry_Blue]?.count_per_meter ||
          0);
      totalBlockAvg =
        totalBlockAvg +
        (blockAverageData[feature as FeatureEnum.blueberry_Blue]?.block_avg ||
          0);
      return row;
    });

    const rows = orderBlueberriesByRipenessOrder({
      arrayToOrder: [...blueberryRows, ...flowerRows],
      blueberryKeyGetter: (a) => a.key,
    });

    const summaryRow = {
      key: 'total',
      count: totalBlueberries + totalFlowers,
      countPerDistance: roundFractional(totalCountPerDistance, 1),
      average: roundFractional(totalBlockAvg, 2),
    };

    return [totalBlueberries, totalFlowers, rows, summaryRow];
  }, [
    blueberryPointData,
    blueberryBlockAvgData,
    blueberryBlockAvgError,
    flowerFeatures,
    meterOrFeetUnitSymbol,
  ]);

  const cellStyles = {
    paddingLeft: 0,
    border: 0,
    borderBottom: `1px solid rgba(255, 255, 255, 0.10)`,
    textAlign: 'right',
    '&:nth-last-of-type': {
      paddingRight: 0,
    },
    '&:first-of-type': {
      textAlign: 'left',
    },
  };

  const headerStyles = {
    ...cellStyles,
    borderBottom: '1px solid rgba(255, 255, 255, 0.25)',
  };

  if (isBlueberryPointLoading || isBlueberryBlockAvgLoading) return null;

  return (
    <Box
      sx={{
        borderRadius: theme.borderRadius.primary,
        background: 'rgba(32, 34, 38, 0.90)',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pointerEvents: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minWidth: '300px',
          height: '2.66rem',
          padding: '0.5rem 1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.10)',
          borderRadius: open ? 0 : theme.borderRadius.primary,
          borderTopLeftRadius: theme.borderRadius.primary,
          borderTopRightRadius: theme.borderRadius.primary,
        }}
      >
        <Typography sx={{ padding: 0 }}>{t('Photo Count')}</Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '1.5rem',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getIcon(FeatureEnum.blueberry, 0.8)}
            </Box>
            <Typography>{totalBlueberries}</Typography>
            {flowerFeatures.length > 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    width: '1.5rem',
                    paddingBottom: '0.1rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getIcon(FeatureEnum.flower, 0.85)}
                </Box>
                <Typography>{totalFlowers}</Typography>
              </>
            )}
          </Box>
          <IconButton
            sx={{ color: theme.colors.common.white, padding: 0 }}
            onClick={() => setOpen(!open)}
          >
            {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open} sx={{ width: '100%', padding: '0rem 1rem' }}>
        <Table size='small' sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyles}>{t('Type')}</TableCell>
              <TableCell sx={headerStyles}>{t('Count')}</TableCell>
              <TableCell sx={headerStyles}>
                {t('Per ' + meterOrFeetUnitSymbol)}
              </TableCell>
              <TableCell sx={{ ...headerStyles }}>{t('Block Avg')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.key}
                sx={{
                  padding: '0.25rem 0.25rem',
                  [summaryRow
                    ? '&:nth-last-of-type(2) td'
                    : '&:nth-last-of-type td']: {
                    border: 0,
                  },
                }}
              >
                <TableCell sx={cellStyles}>{row.icon}</TableCell>
                <TableCell sx={cellStyles}>{row.count}</TableCell>
                <TableCell sx={cellStyles}>{row.countPerDistance}</TableCell>
                <TableCell sx={{ ...cellStyles }}>{row.average}</TableCell>
              </TableRow>
            ))}
            {summaryRow && (
              <TableRow
                sx={{
                  '&:nth-last-of-type td': {
                    borderBottom: 0,
                    borderTop: '1px solid rgba(255, 255, 255, 0.35)',
                  },
                }}
              >
                <TableCell sx={cellStyles}>Total</TableCell>
                <TableCell sx={cellStyles}>{summaryRow.count}</TableCell>
                <TableCell sx={cellStyles}>
                  {summaryRow.countPerDistance}
                </TableCell>
                <TableCell sx={{ ...cellStyles }}>
                  {summaryRow.average}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
};
